<template>
    <div class="container container--utilisateur">  

        <Loader v-if="!ready"></Loader>

        <article class="utilisateur" v-if="ready">

            <header class="utilisateur__heading">
                <h1 class="woocommerce-order-details__title">{{ utilisateur.name }}</h1>
                <div class="utilisateur__heading__email">
                    <p>
                        <strong>{{ utilisateur.email }}</strong>&nbsp;
                        <Badge type="success" v-if="utilisateur.isCreated && utilisateur.isActif">Actif</Badge>
                        <Badge type="secondary" v-if="utilisateur.isCreated && !utilisateur.isActif">Désactivé</Badge>
                        <Badge type="warning" v-if="!utilisateur.isCreated">En cours de création</Badge>
                    </p>
                </div>
                <div class="utilisateur__heading__actions">
                    <a class="button"
                        :href="impersonateLink"
                        :title="'Voir l\'interface en tant que ' + utilisateur.name "
                        v-if="$can('INCARNER_UTILISATEURS') && !isMe">Voir en tant que</a>
                    <button class="button alt" title="Modifier"
                        disabled
                        v-if="$can('CREER_MODIFIER_UTILISATEURS')"
                        v-on:click="/* edit() */">Modifier</button>
                </div>
            </header>  

            <div class="woocommerce-order-details">
                <div class="utilisateur__details">
                    <ul>
                        <li>
                            <strong>Code : </strong> {{ utilisateur.code }}
                        </li>
                        <li>
                            <strong>Profil : </strong> {{ utilisateur.profil.nom }}
                        </li>
                        <li>
                            <strong>Valide du : </strong> <Date :date="utilisateur.validiteDebut" /> au <Date :date="utilisateur.validiteFin" />
                        </li>
                    </ul>
                </div>
            </div>

            <div class="woocommerce-order-details">
                <div class="utilisateur__habilitations">
                    <h3>Habilitations</h3>
                    <div class="woocommerce-address-fields__field-wrapper">
                        <p class="form-row form-row-wide"
                            v-for="habilitation in habilitations"
                            :key="habilitation.id">
                            <input :id="'habilitation_' + habilitation.id" type="checkbox"
                                disabled
                                :value="habilitation.nom"
                                :checked="isHabilitationChecked(habilitation.value)"
                                @input="( $event.target.checked ) ? addHabilitation(habilitation) : removeHabilitation(habilitation)" />
                            <label :for="'habilitation_' + habilitation.id">{{ habilitation.nom }}</label>
                        </p>
                    </div>
                </div>
            </div>

            <div class="utilisateur__clients">
                <h3>Adresses</h3>
                
                <!-- Clients -->
                <ul class="clients"
                    v-for="client in utilisateurClients"
                    :key="client.id">
                    <li class="clients_item">

                        <div class="client">

                            <div class="client__name">
                                <input class="woocommerce-form__input woocommerce-form__input-checkbox"
                                    disabled
                                    type="checkbox"
                                    :id="'client_' + client.id"
                                    :checked="isAllAddressChecked(client)"
                                    @input="( $event.target.checked ) ? addAllClientAddress(client) : removeAllClientAddress(client)" />
                                <strong>Client {{ client.label }}<span v-if="client.clientAddresses.length > 1">({{ client.clientAddresses.length }} adresses)</span><span v-if="client.clientAddresses.length <= 1">({{ client.clientAddresses.length }} adresse)</span></strong>
                            </div>

                            <!-- Adresses client -->
                            <ul class="client__addresses">
                                <li class="client__addresses__item"
                                    v-for="clientAddress in client.clientAddresses"
                                    :id="'clientAddress_' + clientAddress.id"
                                    :key="clientAddress.id">

                                    <div class="client-address"> 
                                        <div class="client-address__content">       
                                            <label class="woocommerce-form__label woocommerce-form__label-for-checkbox"
                                                :for="'clientAddress_' + clientAddress.id">
                                                <input class="woocommerce-form__input woocommerce-form__input-checkbox"
                                                    disabled
                                                    type="checkbox"
                                                    :id="'clientAddress_' + clientAddress.id"
                                                    :checked="isAddressChecked(clientAddress)"
                                                    @input="( $event.target.checked ) ? addClientAddress(clientAddress) : removeClientAddress(clientAddress)" />   
                                                <span class="client-address__content__name">                           
                                                    <strong>{{ clientAddress.name }}</strong><br />
                                                    <span>{{ clientAddress.address.label }}</span><br />
                                                    <small>{{ clientAddress.address.homePhone }}</small>                                
                                                </span>
                                            </label>
                                        </div>
                                        <div class="client-address__default">
                                            <Badge type="info" v-if="isDefaultAddress(clientAddress)">Défaut</Badge>
                                            <a href="#"
                                                class="isDisabled"
                                                title="Choisir cette adresse comme celle par défaut"
                                                v-if="!isDefaultAddress(clientAddress)"
                                                v-on:click.prevent="/* setDefaultAddress(client, clientAddress) */">Choisir par défaut</a>
                                        </div>
                                    </div>

                                </li>
                            </ul>

                        </div>

                    </li>
                </ul>
            </div>

        </article>

    </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'

import api from '@/api/proxyWeb/utilisateurs'
import title from "@/containers/title";
import Badge from '@/components/badge/Badge.vue'
import Date from '@/components/date/Date.vue'
import Loader from '@/components/loader/Loader.vue'
import Router, { routes } from '@/router'

import { createUrl } from '@/router'

import { actions as habilitationActions } from '@/store/habilitations'

export default {

    name: 'Utilisateur',

    data() {
        return {
            utilisateur: null,
            utilisateurAddresses: null,
            utilisateurClients: null,
            utilisateurHabilitations: null,

            changes: 0,
            toastIndex: 0,
            ready: false,

            startDate: null,
            endDate: null,
        }   
    },

    computed: mapState({

        impersonateLink: function() {
            return createUrl(this.$config.ACCOUNT_URL, { action: 'impersonate', user_id: this.utilisateur.userId })
        },

        isMe: function(state) {
            return ( this.utilisateur.userId === state.utilisateurs.current.userId )
        },

        habilitations: state => state.habilitations.all
    }),

    methods: {

        edit: function() {
            Router.push({
                name: routes.UTILISATEUR_EDIT,
                params: { id: this.utilisateur.id } 
            });
        },

        addAllClientAddress: function(client) {

            client.clientAddresses.forEach( clientAddress => {
                this.addClientAddress(clientAddress)
            })
        },

        addClientAddress: function(clientAddress) {

            if ( this.isAddressChecked(clientAddress) ) {
                return
            }

            this.utilisateurAddresses.push(clientAddress)

            api.putAddress(this.utilisateur.id, clientAddress.id)          
                .then(() => this.showUpdateMsg())
        },

        addHabilitation: function(habilitation) {
                
            this.utilisateurHabilitations.push(habilitation)

            api.putHabilitation(this.utilisateur.id, habilitation.id)             
                .then(() => this.showUpdateMsg())
        },

        formatDate: function(datetime) {
            return moment(datetime).format('YYYY-MM-DD')
        },

        isAllAddressChecked: function(client) {
            
            let all = true
            client.clientAddresses.forEach( clientAddress => {
                if ( !this.isAddressChecked(clientAddress) ) {
                    all = false
                }
            })

            return all
        },

        isAddressChecked: function(clientAddress) {
            
            let checked = false
            this.utilisateurAddresses.forEach(address => {
                if ( address.id === clientAddress.id ) {
                    checked = true
                }
            });

            return checked
        },

        isHabilitationChecked: function(habilitationValue) {
            
            let checked = false
            this.utilisateurHabilitations.forEach(habilitation => {
                if ( habilitation.value === habilitationValue ) {
                    checked = true
                }
            });

            return checked
        },

        isDefaultAddress: function(clientAddress) {            
            return ( this.utilisateur.defaultAddressId === clientAddress.id )
        },

        removeAllClientAddress: function(client) {

            client.clientAddresses.forEach( clientAddress => {
                this.removeClientAddress(clientAddress)
            })
        },

        removeClientAddress: function(clientAddress) {

            if ( !this.isAddressChecked(clientAddress) ) {
                return
            }            

            let addresses = []
            this.utilisateurAddresses.forEach(utilisateurAddress => {
                if ( utilisateurAddress.id !== clientAddress.id ) {
                    addresses.push(utilisateurAddress)
                }
            });

            this.utilisateurAddresses = addresses

            api.deleteAddress(this.utilisateur.id, clientAddress.id)
                .then(() => this.showUpdateMsg())
        },

        removeHabilitation: function(habilitation) {

            let habilitations = []
            this.utilisateurHabilitations.forEach(utilisateurHabilitation => {
                if ( utilisateurHabilitation.id !== habilitation.id ) {
                    habilitations.push(utilisateurHabilitation)
                }
            });

            this.utilisateurHabilitations = habilitations

            api.deleteHabilitation(this.utilisateur.id, habilitation.id) 
                .then(() => this.showUpdateMsg())
        },

        setDefaultAddress: function(client, clientAddress) {
            this.utilisateur.defaultClientId = client.id
            this.utilisateur.defaultAddressId = clientAddress.id
            api.update(this.utilisateur.id, {
                    defaultClientId: client.id,
                    defaultAddressId: clientAddress.id
                }) 
                .then(() => this.showUpdateMsg())
        },

        showUpdateMsg: function() {

            this.changes++
            setTimeout(() => {
                this.toastIndex++
                if ( this.toastIndex === this.changes ) {
                    this.$notify.success('Utilisateur sauvegardé')
                }
            }, 1500)
        }
    },
  
    mounted () {

        Promise.all([
                api.get(this.$route.params.id),
                api.getAddresses(this.$route.params.id),
                api.getClients(this.$route.params.id),
                api.getHabilitations(this.$route.params.id),
                this.$store.dispatch(habilitationActions.GET_ALL)
            ])
            .then( data => {
                this.utilisateur = data[0]
                this.utilisateurAddresses = data[1]
                this.utilisateurClients = data[2]
                this.utilisateurHabilitations = data[3]
                this.startDate = this.formatDate(this.utilisateur.validiteDebut)
                this.endDate = this.formatDate(this.utilisateur.validiteFin)
                this.ready = true
                this.setTitle(this.utilisateur.name)
            })
            .catch(error => {
                this.$errorPage(error.reason)
            })
    },

    components: {
        Badge,
        Date,
        Loader
    },

    mixins: [ title ]
}
</script>

<style>

.clients,
.client__addresses {
    list-style: none;
    margin: 0;
    padding: 0;
}

.clients input[type="checkbox"] {
    margin-right: 0.5rem;
}

.client__addresses__item {
    background-color: #f4f4f4;
    margin-bottom: 2px;
    padding: 0.5rem 2rem 0.5rem 6%;
}

.client__name {    
    padding: 1rem;
}

.client-address {
    display: flex;
    flex-direction: row;
}

.client-address__content {
    flex: 1;
}

.client-address__content label {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
}

.client-address__content label input[type="checkbox"] {
    top: 0.5em;
}

.client-address__content__name {
    flex: 1;
}

.client-address__content__name strong {
    line-height: 2em;
}

.client-address__default {
    display: flex;
    flex-direction: row;
    align-items: center;
}

.isDisabled {
  color: currentColor;
  cursor: not-allowed;
  opacity: 0.5;
  text-decoration: none;
}

.utilisateur {
    position: relative;
}

.utilisateur__heading {
    margin-bottom: 2rem;
    padding-top: 4rem; 
}

.utilisateur__heading__actions {
    position: absolute;
    right: 0;
    top: 0;
}

.utilisateur__heading__actions > *:not(:first-child) {
    margin-left: 1rem !important;
}

.utilisateur__clients {
    clear: both;
}

</style>
